import Divider from '@material-ui/core/Divider'
import classNames from 'classnames'
import inRange from 'lodash/inRange'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useContext } from 'react'
import RendererContext from '../../contexts/renderer-context'
import useHierarchyItemTableContext from '../../hooks/use-hierarchy-item-table-context'
import useScrollRange from '../../hooks/use-scroll-range'
import { Column } from '../../models/column'
import Cell from './Cell'
import { makeStyles } from '@material-ui/core'
import { productConfiguration } from '@stores/productConfigurationStore'

export interface RowProps {
  columns: Column[]
  id: string
  rowData: any
  showBorders: boolean
  isActive?: boolean
  enhancedViewCell?: boolean
}

export interface PrivateRowProps {
  stickyColsPosition: { [field: string]: number }
}

const RowRecursive: FC<Omit<RowProps, 'showBorders'> & PrivateRowProps> = memo(
  ({ columns, id, rowData, stickyColsPosition, isActive, enhancedViewCell }) =>
    useObserver(function useHook() {
      const {
        tableContext: {
          tableStore: { columnMeta, priorColumnOffsetWidthByColumnField }
        }
      } = useContext(RendererContext)
      const {
        store: {
          activeItemStore: { colIndex }
        }
      } = useHierarchyItemTableContext()
      const [left, right] = useScrollRange()
      return (
        <>
          {columns?.map((column, index) =>
            column?.columns?.length ? (
              <RowRecursive
                columns={column.columns}
                id={id}
                rowData={rowData}
                key={column.field}
                stickyColsPosition={stickyColsPosition}
                isActive={isActive}
              />
            ) : inRange(priorColumnOffsetWidthByColumnField(column.field), left, right) ||
              column.fixed ||
              column.autoHeight ? (
              <Cell
                offset={priorColumnOffsetWidthByColumnField(column.field)}
                id={id}
                key={column.field}
                column={column}
                rowData={rowData}
                stickyPosition={stickyColsPosition[column.field]}
                visibleColIndex={columnMeta[column.field]?.visibleColIndex || 0}
                isActive={
                  isActive &&
                  colIndex === (columnMeta[column.field]?.visibleColIndex || 0)
                }
                enhancedViewCell={enhancedViewCell}
              />
            ) : (
              <div
                style={{
                  width: column.width,
                  minWidth: column.width,
                  left: priorColumnOffsetWidthByColumnField(column.field)
                }}
              />
            )
          )}
        </>
      )
    })
)

const useStyles = makeStyles(theme => ({
  container: ({ fullHeight }: any) => ({
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: 'white',
    height: fullHeight ? 'calc(100% - 1px)' : 'fit-content',
    transition: 'height 1s',
    width: 'fit-content'
  }),
  containerBorder: {
    borderRight: '1px solid #ededed'
  },
  border: {
    position: 'absolute',
    bottom: 0,
    width: '100%'
  }
}))

const Row: FC<RowProps & PrivateRowProps> = props => {
  return useObserver(function useHook() {
    const { enhancedViewEnabled, isEnhancedBasketView } = productConfiguration
    const classes = useStyles({ fullHeight: isEnhancedBasketView })

    return (
      <div
        className={classNames(classes.container, {
          [classes.containerBorder]: props.showBorders && !enhancedViewEnabled
        })}
      >
        <RowRecursive {...props} />
        {props.showBorders && <Divider variant='fullWidth' className={classes.border} />}
      </div>
    )
  })
}

export default Row
